import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { screen } from "../../state/store.js";
import { adminLoading, adminError, adminDirty, loadAdminEvents } from "../../state/adminData.js";
import { AvailabilityView } from "./AvailabilityView.js";
import { ReportView } from "./ReportView.js";
import { OrdersView } from "./OrdersView.js";
import { BonsView } from "./BonsView.js";
import { PersonalView } from "./PersonalView.js";
import { TischeView } from "./TischeView.js";

// In-App-Verwaltung für Personal-Admins (rollengegated in der StatusBar). Ereignis-basierte
// Auswertung ohne Account-Login: liest den Event-Feed (lokal/Cloud) und schreibt Konfig über
// den Geräte-Token. Geräte/Stationen/voller Speisekarten-Editor bleiben bewusst draußen.

type Tab = "auswertung" | "bestellungen" | "bons" | "personal" | "tische" | "verfuegbarkeit";
const tab = signal<Tab>("auswertung");
const menuOpen = signal(false);

const TABS: { key: Tab; label: string }[] = [
  { key: "auswertung", label: "Auswertung" },
  { key: "bestellungen", label: "Bestellungen" },
  { key: "bons", label: "Bons" },
  { key: "personal", label: "Personal" },
  { key: "tische", label: "Tische" },
  { key: "verfuegbarkeit", label: "Verfügbarkeit" },
];

/** Tabs, die den Event-Feed brauchen (Verfügbarkeit liest die schon gesyncte Speisekarte). */
const NEEDS_EVENTS: Tab[] = ["auswertung", "bestellungen", "bons", "personal", "tische"];

const UNSAVED_MSG = "Ungespeicherte Änderungen verwerfen?";

/** Vor Verlassen des Editors nachfragen, wenn es ungespeicherte Änderungen gibt. */
function confirmLeave(): boolean {
  if (!adminDirty.value) return true;
  if (confirm(UNSAVED_MSG)) {
    adminDirty.value = false;
    return true;
  }
  return false;
}

function pick(key: Tab): void {
  if (key === tab.value) {
    menuOpen.value = false;
    return;
  }
  if (!confirmLeave()) {
    menuOpen.value = false;
    return;
  }
  tab.value = key;
  menuOpen.value = false;
}

function leaveAdmin(): void {
  if (confirmLeave()) screen.value = "order";
}

export function AdminView() {
  // Beim Öffnen der Verwaltung einmal den Event-Feed ziehen (für die Auswertungs-Tabs).
  useEffect(() => {
    void loadAdminEvents();
    // Reload/Schließen mit ungespeicherten Änderungen abfangen; Dirty-Flag beim Verlassen leeren.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (adminDirty.value) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      adminDirty.value = false;
    };
  }, []);

  const active = tab.value;
  const needsEvents = NEEDS_EVENTS.includes(active);
  const activeLabel = TABS.find((t) => t.key === active)?.label ?? "Verwaltung";

  return (
    <div class="admin">
      <header class="admin-bar">
        <button class="admin-back" onClick={leaveAdmin} aria-label="Zurück">
          ‹
        </button>
        <button
          class="admin-burger"
          onClick={() => (menuOpen.value = !menuOpen.value)}
          aria-label="Bereich wählen"
          aria-expanded={menuOpen.value}
        >
          ☰
        </button>
        <h2>{activeLabel}</h2>
        <span class="spacer" />
        <button
          class="admin-refresh"
          disabled={adminLoading.value}
          onClick={() => void loadAdminEvents()}
          aria-label="Aktualisieren"
          title="Aktualisieren"
        >
          {adminLoading.value ? "…" : "↻"}
        </button>
      </header>

      {menuOpen.value && (
        <>
          <div class="admin-menu-backdrop" onClick={() => (menuOpen.value = false)} />
          <nav class="admin-menu">
            {TABS.map((t) => (
              <button
                key={t.key}
                class={active === t.key ? "admin-menu-item active" : "admin-menu-item"}
                onClick={() => pick(t.key)}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </>
      )}

      {needsEvents && adminError.value && (
        <div class="admin-error">Laden fehlgeschlagen: {adminError.value}</div>
      )}

      {active === "auswertung" && <ReportView />}
      {active === "bestellungen" && <OrdersView />}
      {active === "bons" && <BonsView />}
      {active === "personal" && <PersonalView />}
      {active === "tische" && <TischeView />}
      {active === "verfuegbarkeit" && <AvailabilityView />}
    </div>
  );
}
